import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Printer, Award } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { PageHero } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const KID = ["Never go near a pond or pool alone.", "Always ask an adult before entering water.", "Listen to my swim instructor.", "Wear a life jacket when appropriate.", "Never climb over a water-safety fence.", "Tell an adult when I see something dangerous.", "Never jump into water to rescue someone without proper training — get an adult and call for help."];
const PARENT = ["Actively supervise children", "Avoid distracted supervision", "Enroll children in appropriate swimming / water-competency education", "Learn CPR", "Use appropriate life jackets", "Secure pools", "Teach pond safety", "Discuss water safety regularly"];

function Certificate({ cert }) {
  const kid = cert.type === "kid";
  return (
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="rounded-3xl border-[6px] border-double border-[#0096C7] bg-white p-8 md:p-12 text-center shadow-2xl" data-testid="pledge-certificate">
      <img src="/brand/logo.webp" alt="Swim for Rakim" className="w-28 mx-auto" />
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#0096C7] mt-4">Certificate of {kid ? "Promise" : "Pledge"}</p>
      <p className="font-script text-5xl text-[#03045E] mt-3" data-testid="certificate-name">{cert.first_name}</p>
      <p className="font-display uppercase text-2xl md:text-3xl text-[#03045E] mt-3">{kid ? "I made Rakim's Water Safety Promise™" : "I took the Parent Water Safety Pledge™"}</p>
      <div className="flex justify-center mt-6"><span className="w-20 h-20 rounded-full bg-[#FF6B35] text-white flex items-center justify-center shadow-[0_0_25px_rgba(255,107,53,0.5)]"><Award className="w-10 h-10" /></span></div>
      <p className="text-xs text-slate-500 mt-6">No. {cert.certificate_no} • {new Date(cert.created_at).toLocaleDateString()}</p>
      <button onClick={() => window.print()} className="no-print mt-6 rounded-full bg-[#03045E] text-white px-6 py-3 font-bold uppercase text-sm inline-flex items-center gap-2" data-testid="certificate-print-button"><Printer className="w-4 h-4" /> Print / Save</button>
    </motion.div>
  );
}

export default function Promise() {
  const [params, setParams] = useSearchParams();
  const type = params.get("tab") === "parent" ? "parent" : "kid";
  const list = type === "kid" ? KID : PARENT;
  const [checked, setChecked] = useState([]);
  const [name, setName] = useState("");
  const [cert, setCert] = useState(null);
  const switchTo = (t) => { setParams(t === "parent" ? { tab: "parent" } : {}); setChecked([]); setCert(null); };
  const submit = async () => {
    try {
      const { data } = await api.post("/pledges", { type, first_name: name });
      setCert(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  const all = checked.length === list.length;

  return (
    <div data-testid="promise-page">
      <PageHero eyebrow="Promise & Pledge" title={type === "kid" ? "Rakim's Water Safety Promise™" : "Parent Water Safety Pledge™"} script={type === "kid" ? "I promise to…" : "We pledge to…"}>
        <div className="flex gap-2 mt-6">
          {[["kid", "Kids' Promise"], ["parent", "Parent Pledge"]].map(([k, l]) => <button key={k} onClick={() => switchTo(k)} data-testid={`promise-tab-${k}`} className={`rounded-full px-5 py-2 text-sm font-bold uppercase ${type === k ? "bg-[#03045E] text-white" : "bg-white border border-sky-200"}`}>{l}</button>)}
        </div>
      </PageHero>
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        {cert ? <Certificate cert={cert} /> : (
          <div className="rounded-3xl bg-white border border-sky-100 p-6 md:p-10">
            <ul className="space-y-3">
              {list.map((l, i) => (
                <li key={l}>
                  <label className={`flex gap-3 items-start rounded-xl p-3 cursor-pointer transition-colors duration-200 ${checked.includes(i) ? "bg-sky-50" : "hover:bg-slate-50"}`}>
                    <Checkbox checked={checked.includes(i)} onCheckedChange={(v) => setChecked((c) => (v ? [...c, i] : c.filter((x) => x !== i)))} data-testid={`promise-item-${i}`} className="mt-1" />
                    <span className={`font-semibold ${type === "kid" ? "text-lg" : ""}`}>{l}</span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Input placeholder={type === "kid" ? "Your first name" : "Your first name"} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} data-testid="promise-name-input" />
              <button disabled={!all || !name.trim()} onClick={submit} data-testid="promise-submit-button" className="rounded-full bg-[#FF6B35] text-white px-6 py-3 font-bold uppercase text-sm whitespace-nowrap disabled:opacity-50 inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {type === "kid" ? "I made the promise" : "I take the pledge"}</button>
            </div>
            <p className="text-xs text-slate-500 mt-3">Check every line to continue. Only a first name is stored — kids, ask a grown-up first!</p>
          </div>
        )}
      </section>
    </div>
  );
}
